<template>
  <v-form
    :lazy-validation="true"
    @submit.prevent="handleSubmit"
    v-model="valid"
    ref="form"
  >
    <v-card>
      <v-card-text>
        <v-autocomplete
          :items="formulaTemplates"
          :label="$t('formula.form.loadTemplate')"
          item-text="name"
          item-value="formulaTemplateId"
          @change="loadTemplate"
          dense
          clearable
          @click:clear="loadTemplate"
        />

        <div v-if="localPayload.template?.value">
          <v-textarea
            :label="$t('formula.fields.formula')"
            autocomplete="off"
            type="text"
            outlined
            v-model="localPayload.template.value"
            disabled
            dense
          />
          <v-container fluid class="align-center justify-center fill-width">
            <v-row>
              <v-col cols="12" md="12" sm="12">
                <v-data-table
                  :headers="headers"
                  :items="localPayload.formulas"
                  dense
                >
                  <template
                    v-for="h in headers"
                    v-slot:[`item.${h.value}`]="props"
                  >
                    <v-switch
                      v-if="isSwitch(h.value)"
                      :key="'switch' + h.value"
                      v-model="props.item[h.value]"
                      dense
                      :disabled="disableTagSelection(props.item, h.value)"
                      @change="switchUpdate(props.item, h.value)"
                    />

                    <div
                      v-else-if="isDivider(h.value)"
                      :key="'divider' + h.value"
                    ></div>

                    <v-edit-dialog :key="h.value" v-else class="ma-4">
                      {{ props.item[h.value] }}
                      <template v-slot:input>
                        <v-text-field
                          v-if="isFormulaName(h.value)"
                          v-model="props.item[h.value]"
                          single-line
                          :rules="[rules.name]"
                        />

                        <v-autocomplete
                          v-else-if="isDeveuiSelection(props.item, h.value)"
                          v-model="props.item[h.value]"
                          :items="selectableElements.tags"
                          item-text="name"
                          item-value="deveui"
                          dense
                          auto-select-first
                          @change="loadKeys"
                          :rules="[rules.name]"
                          :filter="tagFilter"
                        >
                          <template v-slot:item="data">
                            <v-list-item-content>
                              <v-list-item-title v-html="data.item.name" />
                              <v-list-item-subtitle v-html="data.item.deveui" />
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>

                        <v-combobox
                          v-else-if="isOutputKey(h.value)"
                          v-model="props.item[h.value]"
                          :items="tagKeys(props.item, h.value)"
                          item-text="label"
                          item-value="key"
                          dense
                          @change="updateKey(props.item, h.value, $event)"
                          :rules="[rules.name]"
                        >
                          <template v-slot:item="data">
                            <v-list-item-content>
                              <v-list-item-title v-html="data.item.label" />
                              <v-list-item-subtitle v-html="data.item.key" />
                            </v-list-item-content>
                          </template>
                        </v-combobox>

                        <v-autocomplete
                          v-else-if="isKeySelection(h.value)"
                          v-model="props.item[h.value]"
                          :items="tagKeys(props.item, h.value)"
                          item-text="label"
                          item-value="key"
                          dense
                          auto-select-first
                          @change="updateKey(props.item, h.value, $event)"
                          :disabled="disableTagSelection(props.item, h.value)"
                          :rules="
                            disableTagSelection(props.item, h.value)
                              ? [rules.name]
                              : []
                          "
                        >
                          <template v-slot:item="data">
                            <v-list-item-content>
                              <v-list-item-title v-html="data.item.label" />
                              <v-list-item-subtitle v-html="data.item.key" />
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>

                        <v-autocomplete
                          v-else-if="isConstantSelection(props.item, h.value)"
                          v-model="props.item[h.value]"
                          :items="constants"
                          item-text="name"
                          item-value="name"
                          dense
                          auto-select-first
                          :rules="[rules.name]"
                        >
                          <template v-slot:item="data">
                            <v-list-item-content>
                              <v-list-item-title v-html="data.item.name" />
                              <v-list-item-subtitle v-html="data.item.value" />
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </template>
                    </v-edit-dialog>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row class="pt-2">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="mx-2"
                    fab
                    x-small
                    color="accent"
                    @click="addTableRow"
                    v-on="on"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("formula.import.addTableRow") }}</span>
              </v-tooltip>
              <formula-csv
                v-model="localPayload.formulas"
                :variableNames="variableNames"
                @updateKeys="updatedSelectableKeys"
              />
              <v-spacer />
              <v-btn
                color="primary"
                text
                @click="runTests(null)"
                :disabled="!canSave"
              >
                {{ $t("formula.fields.testButton") }}
              </v-btn>
            </v-row>
            <div v-if="testResults.length > 0">
              <v-col
                cols="12"
                md="12"
                sm="12"
                class="d-flex flex-column justify-center align-center pa-0 ma-0"
              >
                <p :class="(testResult ? 'green' : 'red') + '--text' + ' ma-0'">
                  {{
                    testResult
                      ? $t("formula.form.runtests")
                      : $t("formula.errors.runtests")
                  }}
                </p>
              </v-col>
            </div>
            <div v-if="created">
              <v-col
                cols="12"
                md="12"
                sm="12"
                class="d-flex flex-column justify-center align-center pa-0 ma-0"
              >
                <p class="green--text ma-0">
                  {{ $t("formula.form.createFormulas") }}
                </p>
              </v-col>
            </div>
          </v-container>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn
          color="primary"
          type="button"
          @click="handleSubmit"
          large
          :disabled="!canSave"
        >
          {{ $t("common.create") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import {
  defaultFormulaTemplateFields,
  defaultFormulaHeaderEntry,
  defaultFormulaVariableEntry,
} from "@/_helpers/formulaHelper";
import FormulaCsv from "./FormulaCsv.vue";
import { autoCompleteTagFilter } from "@/_helpers/commonHelper";

export default {
  name: "FormulaImport",

  components: {
    FormulaCsv,
  },

  data() {
    return {
      valid: false,
      variableNames: [],
      testResults: [],
      created: false,
      selectableElements: {
        tags: [],
      },
      localPayload: {
        template: {},
        formulas: [],
      },
      rules: {
        name: (v) => !!v || this.$t("validation.requiredSingle"),
      },
    };
  },

  computed: {
    ...mapState("formulas", ["formulaTemplates", "constants"]),
    ...mapGetters("formulas", ["getConstantByName"]),

    headers() {
      let h = [];
      this.formHeaders.forEach((x) => {
        h.push({ text: this.$t("formula.form." + x), value: x });
      });
      this.variableNames.forEach((x) => {
        h.push({ text: "", value: `${x}-spacer`, sortable: false });
        this.varHeaders.forEach((y) => {
          h.push({
            text: `${x} - ${this.$t("formula.form." + y)}`,
            value: `${x}-${y}`,
          });
        });
      });
      return h;
    },

    testResult() {
      return (
        this.testResults.length > 0 && this.testResults.every((x) => x.success)
      );
    },

    varHeaders() {
      return Object.keys(defaultFormulaVariableEntry);
    },

    formHeaders() {
      return Object.keys(defaultFormulaHeaderEntry);
    },

    canSave() {
      return (
        this.localPayload.template?.value &&
        this.localPayload.formulas?.length > 0
      );
    },
  },

  methods: {
    ...mapActions("formulas", [
      "getFormulaTemplates",
      "getConstants",
      "getVariablesFromFormulaString",
      "testFormula",
      "createFormulaUsingImport",
    ]),
    ...mapActions("tag", ["getTagsWithInformation", "getKeysForTag"]),
    ...mapActions("alert", ["success", "error"]),

    isFormulaName(tableHeader) {
      return tableHeader.includes(this.formHeaders.at(0));
    },

    isSwitch(tableHeader) {
      return (
        tableHeader.includes(`-${this.varHeaders.at(0)}`) ||
        tableHeader.includes(`-${this.varHeaders.at(3)}`)
      );
    },

    isDivider(tableHeader) {
      return tableHeader.includes(`-spacer`);
    },

    isDeveuiSelection(formula, tableHeader) {
      if (tableHeader.includes(this.formHeaders.at(1))) {
        return true;
      }

      let identifier = tableHeader.split("-").at(0);
      return (
        !formula[`${identifier}-${this.varHeaders.at(0)}`] &&
        tableHeader.includes(`-${this.varHeaders.at(1)}`)
      );
    },

    isConstantSelection(formula, tableHeader) {
      let identifier = tableHeader.split("-").at(0);
      return (
        formula[`${identifier}-${this.varHeaders.at(0)}`] &&
        tableHeader.includes(`-${this.varHeaders.at(1)}`)
      );
    },

    isKeySelection(tableHeader) {
      return tableHeader.includes(`-${this.varHeaders.at(2)}`);
    },

    isOutputKey(tableHeader) {
      return tableHeader.includes(this.formHeaders.at(2));
    },

    disableTagSelection(formula, tableHeader) {
      let identifier = tableHeader.split("-").at(0);
      return (
        formula[`${identifier}-${this.varHeaders.at(0)}`] &&
        (tableHeader.includes(`-${this.varHeaders.at(2)}`) ||
          tableHeader.includes(`-${this.varHeaders.at(3)}`))
      );
    },

    tagKeys(formula, tableHeader) {
      if (tableHeader.includes(this.formHeaders.at(2))) {
        return this.selectableElements[formula[this.formHeaders.at(1)]] ?? [];
      }
      let identifier = tableHeader.split("-").at(0);
      return (
        this.selectableElements[
          formula[`${identifier}-${this.varHeaders.at(1)}`]
        ] ?? []
      );
    },

    tagFilter(item, queryText) {
      return autoCompleteTagFilter(item, queryText);
    },

    async loadKeys(deveui) {
      if (deveui == undefined || deveui == null) return;
      this.selectableElements[deveui] ??=
        (await this.getKeysForTag({ tagId: deveui })) ?? [];
    },

    async updateKey(formula, tableHeader, key) {
      if (key) {
        if (typeof key == "string") {
          formula[tableHeader] = key;
        }
        if (typeof key == "object" && key.key) {
          formula[tableHeader] = key.key;
        }
      }
    },

    switchUpdate(formula, tableHeader) {
      if (tableHeader.includes(`-${this.varHeaders.at(0)}`)) {
        let identifier = tableHeader.split("-").at(0);
        formula[`${identifier}-${this.varHeaders.at(1)}`] = "";
        formula[`${identifier}-${this.varHeaders.at(2)}`] = "";
      }
    },

    async handleSubmit() {
      this.created = false;
      if (!this.$refs.form.validate()) {
        this.valid = false;
        return;
      }

      let payload = await this.buildPayload();
      if (payload == null || payload == undefined) return;

      let tests = await this.runTests(payload, false);
      if (!tests) {
        this.error(this.$t("formula.errors.runtests"));
        return;
      }

      if (await this.createFormulaUsingImport(payload)) {
        this.success(this.$t("formula.form.createFormulas"));
        this.created = true;
        this.localPayload.formulas = [];
        return;
      }
      this.error(this.$t("formula.errors.createFormula"));
    },

    async runTests(payload, notification = true) {
      this.created = false;
      this.testResults = [];
      if (payload == undefined || payload == null) {
        payload = await this.buildPayload();
        if (payload == null) {
          this.error(this.$t("formula.errors.runtests"));
          return;
        }
      }

      for (var formula of payload.formulas)
        this.testResults.push(await this.testFormula(formula));

      if (notification) {
        this.testResults.every((x) => x.success)
          ? this.success(this.$t("formula.form.runtests"))
          : this.error(this.$t("formula.errors.runtests"));
      }
      return this.testResults.every((x) => x.success);
    },

    async loadTemplate(val) {
      this.created = false;
      let template = this.formulaTemplates.find(
        (x) => x.formulaTemplateId == val
      );

      this.localPayload.template =
        template != undefined ? template : defaultFormulaTemplateFields;
      this.localPayload.formulas = [];
      this.testResults = [];

      if (template != undefined) {
        this.variableNames = await this.getVariablesFromFormulaString(
          template.value
        );
      }
    },

    async buildPayload() {
      let payload = { formulas: [] };
      for (var localFormula of this.localPayload.formulas) {
        let formula = {
          name: localFormula.formulaName,
          description: this.localPayload.template.description,
          value: this.localPayload.template.value,
          awaitAllInput: this.localPayload.template.awaitAllInput,
          active: true,
          outputDeveui: localFormula.outputDeveui,
          outputKey: localFormula.outputSensor,
          resources: [],
        };

        for (var identifier of this.variableNames) {
          let variableKeys = Object.keys(localFormula).filter(
            (key) =>
              key.includes(`${identifier}-`) &&
              this.varHeaders.some((element) => key.includes(element))
          );
          if (variableKeys.length < this.varHeaders.length) {
            this.error(this.$t("formula.errors.payloadBuilding.default"));
            return null;
          }

          let isConstant = localFormula[variableKeys.at(0)];
          let triggerOnChange = localFormula[variableKeys.at(3)];
          let deveui = null;
          let key = null;
          let constantId = null;

          if (isConstant) {
            let constant = await this.getConstantByName(
              localFormula[variableKeys.at(1)]
            );
            if (constant == undefined) {
              this.error(this.$t("formula.errors.payloadBuilding.noConstant"));
              return null;
            }
            constantId = constant.constantId;
          } else {
            deveui = localFormula[variableKeys.at(1)];
            key = localFormula[variableKeys.at(2)];
          }

          if (triggerOnChange != undefined && (constantId || (deveui && key))) {
            let resource = {
              identifier: identifier,
              deveui: deveui,
              key: key,
              constantId: constantId,
              triggerOnChange: triggerOnChange,
            };
            formula.resources.push(resource);
            continue;
          }
          this.error(this.$t("formula.errors.payloadBuilding.default"));
          return null;
        }
        payload.formulas.push(formula);
      }
      return payload;
    },

    addTableRow() {
      this.created = false;
      this.testResults = [];
      let row = { ...defaultFormulaHeaderEntry };
      for (var name of this.variableNames) {
        this.varHeaders.forEach((y) => {
          row[`${name}-${y}`] = defaultFormulaVariableEntry[y];
        });
      }
      this.localPayload.formulas.push(row);
    },

    async updatedSelectableKeys() {
      for (var formula of this.localPayload.formulas) {
        await this.loadKeys(formula.outputDeveui);
        for (var name of this.variableNames) {
          if (!formula[`${name}-${this.varHeaders.at(0)}`])
            await this.loadKeys(formula[`${name}-${this.varHeaders.at(1)}`]);
        }
      }
    },
  },

  async created() {
    await this.getFormulaTemplates();
    await this.getConstants();
    this.selectableElements.tags = await this.getTagsWithInformation();
  },
};
</script>
